import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material'
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Mic as MicIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material'

import { useSessionPharmacist, usePharmacistsQuery } from '@/features/auth'
import { useSidebarYakurekiList } from './useSidebarYakurekiList'
import { useMyPharmacyQuery } from '@/features/yakureki'
import {
  RECORDING_NAV_BLOCK_HINT,
  useActiveRecording,
} from '@/apps/latest/shared/contexts/ActiveRecordingContext'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import {
  ICON_SIZE,
  UI_FONT_SIZE,
  FONT_WEIGHT,
  scaledFontSize,
} from '@/shared/styles/typography'
import {
  blocksOwnRecording,
  REMOTE_STATE_LABEL,
} from '@/features/remoteGuidance'
import { guidancePhaseOf } from '@/shared/types/yakurekiCore'
import { RADIUS } from '@/shared/styles/radii'
import type {
  GuidancePhase,
  Yakureki,
  YakurekiStatus,
} from '@/shared/types/yakurekiCore'

import type { IconKey } from './IconBar'
import { KnowledgePane } from './KnowledgePane'
import { HomecarePane } from './HomecarePane'
import { MessagesPane } from './MessagesPane'
import { SidebarDateStrip, isSameDay } from './SidebarDateStrip'
import { SettingsPane } from './SettingsPane'

interface Props {
  active: IconKey
  // route 遷移時に呼ぶ任意 callback (スマホの overlay Drawer を閉じる用)。
  // 患者選択 / 録音開始の navigate と連動。日付移動や複数選択トグルでは呼ばない。
  onNavigate?: () => void
}

// 文字サイズモード追従 (ADR-0019): サイドバー患者リストは scaledFontSize で拡大する.
// styled へは静的オブジェクトでなく theme callback で渡す (font-scale.md 禁則).
const Section = styled(Box)(({ theme }) => ({
  padding: '8px 12px',
  fontSize: scaledFontSize('bodySm')(theme),
  color: colorTokens.fg.default,
}))

const Heading = styled(Typography)(({ theme }) => ({
  color: colorTokens.fg.muted,
  fontSize: scaledFontSize('tag')(theme),
  fontWeight: FONT_WEIGHT.medium,
  marginBottom: 8,
}))

const ComingSoonRow = styled(Box)(({ theme }) => ({
  padding: '12px 8px',
  fontSize: scaledFontSize('tag')(theme),
  color: colorTokens.fg.faint,
  fontStyle: 'italic',
}))

const FILTER_ALL = '__all__'

// ─── filter row SSoT ───
// HomePane に積まれる 3 row (date / pharmacist / search) の見た目を揃えるための spec.
// 3 row の中身は MUI primitive が異なる (Stack / Select=OutlinedInput / InputBase) ため、
// 「同じ枠 (border/bg/radius/font/height) を被せる」 ことで視覚的に揃える戦略を取る.
//
// 設計判断:
// - date strip は「label を sidebar bg に溶け込ませる」 user 指示で枠を**意図的に外している** (= ICON_GAP_PX 適用のみ).
// - 他の左ペインは管理栄養士のお客様の絞り 1 か所 (features/nutrition/NutritionSidebar) だけが同じ枠を使う (ADR-0086).
//   atom 化はしない (YAGNI). 使い手が増えたら `src/components/atoms/` に昇格.
export const FILTER_ROW_SX = {
  height: 28,
  width: '100%',
  mb: 0.75,
  px: 1,
  borderRadius: `${RADIUS.md}px`,
  border: `1px solid ${colorTokens.border.default}`,
  background: colorTokens.bg.default,
  // 文字サイズモード追従 (sx の property callback で theme.fontScale を読む)
  fontSize: scaledFontSize('tag'),
  color: colorTokens.fg.default,
} as const

// 左 icon の幾何. Select の ▾ と Input の startAdornment アイコンを **同 x 座標** に揃えるため一元化.
//   icon left edge = FILTER_ROW_SX.px (= 8px) — 枠との余白
//   icon size      = ICON_SIZE.sm        (= 14px)
//   gap to text    = 4px
//   text padding-left = LEFT + SIZE + GAP = 8 + 14 + 4 = 26px
const ICON_LEFT_PX = 8
export const ICON_TEXT_GAP_PX = 4
const ICON_SLOT_PL_PX =
  ICON_LEFT_PX + 14 /* = ICON_SIZE.sm */ + ICON_TEXT_GAP_PX // = 26

// MUI <Select> を FILTER_ROW_SX 枠 + 左 icon パターンに馴染ませるための上書き集.
// 内訳:
//  - notchedOutline 消去 (= MUI 標準 fieldset border が FILTER_ROW_SX の border と重複するため)
//  - ▾ icon を右 → 左 + size を 14px + 色 fg.muted + click 透過 (= 枠 click で開くため)
//  - select の text 領域 padding を ICON_SLOT_PL_PX に合わせる
const SELECT_BOX_OVERRIDES_SX = {
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiSelect-icon': {
    left: ICON_LEFT_PX,
    right: 'auto',
    top: 'calc(50% - 7px)',
    fontSize: 14, // = ICON_SIZE.sm (Input の SearchIcon と同 size)
    color: colorTokens.fg.muted,
    pointerEvents: 'none',
  },
  '& .MuiSelect-select': {
    py: 0.25,
    pl: `${ICON_SLOT_PL_PX}px !important`,
    pr: 1,
  },
} as const

export const ContextPane = ({ active, onNavigate }: Props) => {
  if (active === 'home') return <HomePane onNavigate={onNavigate} />
  if (active === 'homecare') return <HomecarePane onNavigate={onNavigate} />
  if (active === 'messages') return <MessagesPane onNavigate={onNavigate} />
  if (active === 'knowledge') return <KnowledgePane onNavigate={onNavigate} />
  if (active === 'settings') return <SettingsPane />
  return null
}

// status → 小さな dot の色と塗り方 (Notion 流: 色で状態を示し本文は中性色)。
// 色相だけに頼らず「塗り(filled) / 輪郭(outline)」でも区別し、緑(completed) を
// 完了系で唯一の有彩色にして「灰か緑か」の迷いを消す。未録音/却下は空=輪郭のみ。
const STATUS_DOT_STYLE: Record<
  YakurekiStatus,
  { color: string; variant: 'filled' | 'outline' }
> = {
  pending: { color: colorTokens.fg.disabled, variant: 'outline' },
  recording: { color: colorTokens.state.recordingIcon, variant: 'filled' },
  processing: { color: colorTokens.state.warnIcon, variant: 'filled' },
  completed: { color: colorTokens.state.successFg, variant: 'filled' },
  copied: { color: colorTokens.fg.muted, variant: 'filled' },
  dismissed: { color: colorTokens.fg.disabled, variant: 'outline' },
  failed: { color: colorTokens.state.dangerIcon, variant: 'filled' },
  failed_short: { color: colorTokens.state.dangerIcon, variant: 'filled' },
}

const StatusDot = ({ status }: { status: YakurekiStatus }) => {
  const { color, variant } = STATUS_DOT_STYLE[status]
  const isOutline = variant === 'outline'
  return (
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
        boxSizing: 'border-box',
        backgroundColor: isOutline ? 'transparent' : color,
        border: isOutline ? `1px solid ${color}` : 'none',
      }}
    />
  )
}

// 遠隔服薬指導の札 (back log/250)。頼む店は「遠隔：依頼中」、遠隔の店は「〇〇店から 着席」
const formatRemoteTag = (y: Yakureki): string => {
  const remote = y.remote
  if (!remote || remote.state === 'cancelled') return ''
  const label = REMOTE_STATE_LABEL[remote.state]
  return remote.direction === 'incoming'
    ? `${remote.from_pharmacy_name}から ${label}`
    : `遠隔：${label}`
}

const formatHeadline = (y: Yakureki): string => {
  const number = y.queue_number ? y.queue_number : ''
  const name = y.patient_name || y.label || '(無名)'
  return number ? `${number} ${name}` : name
}

// 指導フェーズ群の節見出し (Section heading 字格: tag/500/fg.muted + 件数).
// 3 群とも click / Enter で折りたたみトグル (chevron 回転は 完了 header と同意匠).
// sidebar.md「section 間の divider は置かない」— spacing と heading だけで分離.
const PhaseHeading = ({
  label,
  count,
  expanded,
  onToggle,
}: {
  label: string
  count: number
  expanded: boolean
  onToggle: () => void
}) => (
  <Box
    role="button"
    tabIndex={0}
    aria-expanded={expanded}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onToggle()
      }
    }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.25,
      mt: 0.5,
      px: 1,
      py: 0.5,
      borderRadius: `${RADIUS.md}px`,
      cursor: 'pointer',
      color: colorTokens.fg.muted,
      '&:hover': { backgroundColor: 'action.hover' },
      '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: '-2px',
      },
    }}
  >
    <ExpandMoreIcon
      sx={{
        fontSize: ICON_SIZE.sm,
        transition: 'transform 120ms',
        transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
      }}
    />
    <Typography
      sx={{
        fontSize: scaledFontSize('tag'),
        fontWeight: FONT_WEIGHT.medium,
        color: 'inherit',
      }}
    >
      {label} ({count})
    </Typography>
  </Box>
)

const HomePane = ({ onNavigate }: { onNavigate?: () => void }) => {
  const navigate = useNavigate()
  // route 遷移 + onNavigate (Drawer クローズ等) をまとめる. 患者選択 / 録音開始で使う。
  const go = (to: string) => {
    onNavigate?.()
    navigate(to)
  }
  // 現在開いている患者 = /soap/:requestId route param (= App.tsx の IndexPage route).
  // この id の row を §4.1 ピル状アクティブ行 (bg.muted + weight 500) で強調する.
  const { requestId: openPatientId } = useParams<{ requestId: string }>()
  const [search, setSearch] = useState('')
  // 遠隔の店だけ: [すべて | 遠隔 (新着 N)] (back log/250 決定 18)
  const { data: myPharmacy } = useMyPharmacyQuery()
  const isRemotePharmacy = !!myPharmacy?.remote_guidance?.is_remote_pharmacy
  const [remoteOnly, setRemoteOnly] = useState(false)
  // 指導フェーズ 3 群の折りたたみ state. 既定は 指導前・指導済 = 開 / 完了 = 閉
  // (= 普段の作業対象は見せ、終わったものは隠す).
  const [phaseExpanded, setPhaseExpanded] = useState<
    Record<GuidancePhase, boolean>
  >({
    pre: true,
    post: true,
    done: false,
  })
  const togglePhase = (phase: GuidancePhase) =>
    setPhaseExpanded((prev) => ({ ...prev, [phase]: !prev[phase] }))
  // multi-select mode: まとめて録音 CTA を出すために選択モード state を持つ.
  // toggle で OFF にすると selectedIds は捨てる (= 操作意図の clean reset).
  const [multiSelect, setMultiSelect] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // カスタム録音 menu (= Heading 「+」 hover-only chrome の popover anchor).
  // 28 spec §4.1 / 5 原則 #2 「hover-only chrome」 準拠. 95% は薬歴 row click なので
  // 新規/まとめて録音はここに隔離して主役 patient list の縦スペースを取らない.
  const [recordMenuAnchor, setRecordMenuAnchor] = useState<HTMLElement | null>(
    null
  )
  const { isActiveRecording } = useActiveRecording()

  const { pharmacists } = usePharmacistsQuery()
  const pharmacistList = useMemo(
    () => (pharmacists?.pharmacists ?? []).filter((p) => p.is_own),
    [pharmacists]
  )
  const { sessionPharmacistId } = useSessionPharmacist(pharmacistList)

  const [filterPharmacistId, setFilterPharmacistId] = useState<string>(
    () => localStorageWrapper.getItem('filterPharmacistId') || FILTER_ALL
  )

  const handleFilterChange = (next: string) => {
    setFilterPharmacistId(next)
    localStorageWrapper.setItem('filterPharmacistId', next)
  }

  // 2 秒 delta ポーリングで sidebar を時間駆動同期する (= 他経路で作成された event も reload なしで surface).
  const { data, isLoading, reload } = useSidebarYakurekiList()
  // 手動リロード中フラグ (= icon を回し、 hover 外でも可視に保つ).
  const [isReloading, setIsReloading] = useState(false)
  const handleReload = async () => {
    if (isReloading) return
    setIsReloading(true)
    try {
      await reload()
    } finally {
      setIsReloading(false)
    }
  }

  // ─── 日付フィルタ ─── (= legacy useDrawerFilters の 1 機能を移植). 帯は SidebarDateStrip.
  const [date, setDate] = useState<Date>(() => new Date())

  const filtered = useMemo(() => {
    const all = data ?? []
    const byDate = all.filter((y) => isSameDay(new Date(y.created_at), date))
    // 指導前 (pre) の row には担当薬剤師フィルタを適用しない (04-apps/kakeru.md):
    // pharmacist_id は録音時に確定するため指導前は原則 null で、 フィルタ有効時に
    // 「これから対応すべき患者」がリストから消える. 指導前は店舗の共有キューであり、
    // フィルタは自分の作業分 (指導済 / 完了) にだけ意味を持つ. 日付・検索は全 row 適用.
    const byPharmacist =
      filterPharmacistId === FILTER_ALL
        ? byDate
        : byDate.filter(
            (y) =>
              guidancePhaseOf(y.status) === 'pre' ||
              y.pharmacist_id === filterPharmacistId
          )
    const byRemote =
      isRemotePharmacy && remoteOnly
        ? byPharmacist.filter((y) => y.remote?.direction === 'incoming')
        : byPharmacist
    if (!search) return byRemote
    const q = search.toLowerCase()
    return byRemote.filter(
      (y) =>
        y.patient_name?.toLowerCase().includes(q) ||
        y.queue_number?.includes(search) ||
        y.label?.toLowerCase().includes(q)
    )
  }, [data, date, filterPharmacistId, search, isRemotePharmacy, remoteOnly])
  const remoteNewCount = (data ?? []).filter(
    (y) => y.remote?.direction === 'incoming' && y.remote.state === 'requested'
  ).length

  // ─── 指導フェーズグルーピング (04-apps/kakeru.md: 3 分割) ───
  // 指導前 (pre) = pending / failed_short (これから指導する患者 = 店舗の共有キュー)
  // 指導済 (post) = recording / processing / completed / failed (録音以降・コピー前)
  // 完了 (done) = copied / dismissed. 作業が一段落した row を退避し既定で折りたたむ
  // (= legacy の「完了を表示」トグルの Notion 版. トグル UI を増やさない).
  // dismissed を done に含めるのは legacy の mark-dismissed 経由で立った status が
  // 「対応中」に混ざる不整合の解消 (latest 自身は dismissed を立てない).
  const { pre, post, done } = useMemo(() => {
    const pre: Yakureki[] = []
    const post: Yakureki[] = []
    const done: Yakureki[] = []
    filtered.forEach((y) => {
      const phase = guidancePhaseOf(y.status)
      if (phase === 'pre') pre.push(y)
      else if (phase === 'done') done.push(y)
      else post.push(y)
    })
    return { pre, post, done }
  }, [filtered])

  const showPharmacistSelect = pharmacistList.length >= 1

  // 録音可能な薬歴のみ multi-select 対象 (= pending / failed_short).
  // 既に録音中 / 完了 / 処理中は同時録音不可.
  // 遠隔に依頼中 (頼む店) も外す: 遠隔の店の通話の音声と二重に書くため。患者画面の
  // マイクを止める線 (pages/index.tsx) と同じ 1 つの関数で決める
  const isSelectableForGroup = (y: Yakureki): boolean =>
    (y.status === 'pending' || y.status === 'failed_short') &&
    !blocksOwnRecording(y.remote)

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleGroupRecordStart = () => {
    if (selectedIds.size < 2) return
    const ids = Array.from(selectedIds).join(',')
    go(`/recording/group?ids=${ids}`)
    setMultiSelect(false)
    setSelectedIds(new Set())
  }

  // 患者 row の描画 (対応中 / 完了 両グループで共有). active row (= 現在開いている
  // 患者) は §4.1 ピル状アクティブ行 (bg.muted + weight 500) で強調する.
  const renderRow = (y: Yakureki) => {
    const selectable = isSelectableForGroup(y)
    const checked = selectedIds.has(y.id)
    const rowDisabled = isActiveRecording || (multiSelect && !selectable)
    const isActive = y.id === openPatientId
    return (
      <Tooltip
        key={y.id}
        title={isActiveRecording ? RECORDING_NAV_BLOCK_HINT : ''}
        placement="right"
        disableHoverListener={!isActiveRecording}
      >
        <Box
          role="button"
          tabIndex={rowDisabled ? -1 : 0}
          aria-disabled={rowDisabled}
          aria-current={isActive ? 'true' : undefined}
          data-state={isActive ? 'active' : undefined}
          onClick={() => {
            if (rowDisabled) return
            if (multiSelect) {
              toggleSelected(y.id)
            } else {
              go(`/soap/${y.id}`)
            }
          }}
          onKeyDown={(e) => {
            if (rowDisabled) return
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (multiSelect) toggleSelected(y.id)
              else go(`/soap/${y.id}`)
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: `${RADIUS.md}px`,
            cursor: rowDisabled ? 'not-allowed' : 'pointer',
            opacity: rowDisabled ? 0.5 : 1,
            // 選択中患者 = §4.1 ピル状アクティブ行 (warm gray, 青背景にしない).
            backgroundColor: isActive ? colorTokens.bg.muted : undefined,
            '&:hover':
              rowDisabled || isActive
                ? undefined
                : { backgroundColor: 'action.hover' },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '-2px',
            },
          }}
        >
          {multiSelect && selectable ? (
            // multi-select 中の selectable row: checkbox を**常時表示** (checked /
            // unchecked とも) して status dot と置換する. 選択モードでは「選ぶ」が
            // 主操作に昇格するため hover 隠しにしない (hover-only-chrome.md 対象表.
            // 2026-07-23 アイセイ FB「チェックボックスがマウスオーバー以外で見えない」).
            <Box
              sx={{
                width: 18,
                height: 18,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Checkbox
                size="small"
                checked={checked}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleSelected(y.id)}
                sx={{ p: 0 }}
                inputProps={{
                  'aria-label': `${formatHeadline(y)} を選択`,
                }}
              />
            </Box>
          ) : (
            // 通常モード or 非選択可能 row: 6px dot を direct 配置 (= 余分な
            // wrapper 無し). previous compact layout 維持.
            <StatusDot status={y.status} />
          )}
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: scaledFontSize('label'),
              // active row は weight 500 (§4.1). 非 active は既存 medium.
              fontWeight: isActive ? 500 : FONT_WEIGHT.medium,
              color: colorTokens.fg.default,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {formatHeadline(y)}
          </Typography>
          {formatRemoteTag(y) && (
            // 札は行の半分まで。店名が長くても患者名を押し出さない (札の方を省略する。
            // 全文は患者画面の下の行に出る)
            <Typography
              sx={{
                flexShrink: 0,
                maxWidth: '50%',
                fontSize: scaledFontSize('tag'),
                color: colorTokens.fg.muted,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formatRemoteTag(y)}
            </Typography>
          )}
        </Box>
      </Tooltip>
    )
  }

  return (
    // 上段 (薬歴見出し + 日付ナビ + 薬剤師フィルタ + 検索) は固定、 下段の患者リストだけを流す.
    // 絞り込みは「リストを見ながら掛け直す」操作なので、 リストと一緒に画面外へ出てはいけない.
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Section
        sx={{
          flexShrink: 0,
          // hover で Heading の '+' / 更新 icon を出すための parent. group hover pattern.
          '&:hover .record-menu-trigger, &:hover .list-reload-trigger, & .record-menu-trigger.is-open':
            {
              opacity: 1,
            },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
            minHeight: 24,
          }}
        >
          <Heading sx={{ flex: 1, mb: 0 }}>
            薬歴
            {!isLoading && (
              <Typography
                component="span"
                sx={{ ml: 0.5, fontSize: 'inherit' }}
              >
                ({filtered.length})
              </Typography>
            )}
          </Heading>
          {!multiSelect && !isActiveRecording && (
            <>
              <Tooltip title="リストを更新" placement="left">
                <IconButton
                  className="list-reload-trigger"
                  size="small"
                  onClick={() => void handleReload()}
                  disabled={isReloading}
                  aria-label="薬歴リストを更新 (キャッシュをクリア)"
                  sx={{
                    width: 22,
                    height: 22,
                    p: 0,
                    mr: 0.25,
                    opacity: isReloading ? 1 : 0,
                    transition: 'opacity 120ms',
                    color: colorTokens.fg.muted,
                    '& svg': isReloading
                      ? { animation: 'sidebarReloadSpin 0.8s linear infinite' }
                      : undefined,
                    '@keyframes sidebarReloadSpin': {
                      to: { transform: 'rotate(360deg)' },
                    },
                  }}
                >
                  <RefreshIcon sx={{ fontSize: ICON_SIZE.sm }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="カスタム録音" placement="left">
                <IconButton
                  className={`record-menu-trigger${
                    recordMenuAnchor ? ' is-open' : ''
                  }`}
                  size="small"
                  onClick={(e) => setRecordMenuAnchor(e.currentTarget)}
                  aria-label="カスタム録音メニューを開く"
                  sx={{
                    width: 22,
                    height: 22,
                    p: 0,
                    opacity: 0,
                    transition: 'opacity 120ms',
                    color: colorTokens.fg.muted,
                  }}
                >
                  <AddIcon sx={{ fontSize: ICON_SIZE.sm }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <Menu
          anchorEl={recordMenuAnchor}
          open={Boolean(recordMenuAnchor)}
          onClose={() => setRecordMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 180,
                boxShadow: 1,
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setRecordMenuAnchor(null)
              go('/recording/new')
            }}
            sx={{ fontSize: UI_FONT_SIZE.label, minHeight: 32 }}
          >
            <ListItemIcon sx={{ minWidth: 26 }}>
              <MicIcon
                sx={{ fontSize: ICON_SIZE.sm, color: colorTokens.fg.muted }}
              />
            </ListItemIcon>
            <ListItemText
              primary="新規録音"
              primaryTypographyProps={{
                fontSize: UI_FONT_SIZE.label,
                color: colorTokens.fg.default,
              }}
            />
          </MenuItem>
          <MenuItem
            onClick={() => {
              setRecordMenuAnchor(null)
              setMultiSelect(true)
              // 選択対象 (pending / failed_short) は指導前群にいるため、
              // 折りたたまれていても選択モード入りで開く.
              setPhaseExpanded((prev) => ({ ...prev, pre: true }))
            }}
            sx={{ fontSize: UI_FONT_SIZE.label, minHeight: 32 }}
          >
            <ListItemIcon sx={{ minWidth: 26 }}>
              <GroupIcon
                sx={{ fontSize: ICON_SIZE.sm, color: colorTokens.fg.muted }}
              />
            </ListItemIcon>
            <ListItemText
              primary="まとめて録音"
              primaryTypographyProps={{
                fontSize: UI_FONT_SIZE.label,
                color: colorTokens.fg.default,
              }}
            />
          </MenuItem>
        </Menu>
        {isRemotePharmacy && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[
              { value: false, label: 'すべて' },
              {
                value: true,
                label: remoteNewCount
                  ? `遠隔 (新着 ${remoteNewCount})`
                  : '遠隔',
              },
            ].map((tab) => (
              <Box
                key={tab.label}
                component="button"
                type="button"
                onClick={() => setRemoteOnly(tab.value)}
                sx={{
                  border: 'none',
                  borderRadius: `${RADIUS.md}px`,
                  px: 1,
                  py: 0.25,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: scaledFontSize('tag'),
                  bgcolor:
                    remoteOnly === tab.value
                      ? colorTokens.bg.muted
                      : 'transparent',
                  color:
                    remoteOnly === tab.value
                      ? colorTokens.fg.default
                      : colorTokens.fg.muted,
                }}
              >
                {tab.label}
              </Box>
            ))}
          </Box>
        )}
        <SidebarDateStrip date={date} onChange={setDate} />
        {showPharmacistSelect && (
          <Select
            size="small"
            value={
              filterPharmacistId === FILTER_ALL
                ? FILTER_ALL
                : pharmacistList.some((p) => p.id === filterPharmacistId)
                ? filterPharmacistId
                : sessionPharmacistId || FILTER_ALL
            }
            onChange={(e) => handleFilterChange(e.target.value)}
            displayEmpty
            sx={{
              ...FILTER_ROW_SX,
              px: 0, // Select は内部 padding を SELECT_BOX_OVERRIDES_SX で管理
              ...SELECT_BOX_OVERRIDES_SX,
            }}
          >
            <MenuItem
              value={FILTER_ALL}
              sx={{ fontSize: scaledFontSize('tag') }}
            >
              全員の薬歴
            </MenuItem>
            {pharmacistList.map((p) => (
              <MenuItem
                key={p.id}
                value={p.id}
                sx={{ fontSize: scaledFontSize('tag') }}
              >
                {p.display_name}
              </MenuItem>
            ))}
          </Select>
        )}
        <InputBase
          placeholder="患者名 / 受付番号"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          startAdornment={
            // adornment の mr で text gap (= ICON_TEXT_GAP_PX) を作る.
            // Select 側の ▾ も同 ICON_LEFT_PX / 同 ICON_SIZE.sm に揃えてある (= SELECT_BOX_OVERRIDES_SX).
            <InputAdornment
              position="start"
              sx={{ mr: `${ICON_TEXT_GAP_PX}px` }}
            >
              <SearchIcon
                sx={{ fontSize: ICON_SIZE.sm, color: colorTokens.fg.muted }}
              />
            </InputAdornment>
          }
          sx={{
            ...FILTER_ROW_SX,
            // Input の px は left icon の x 座標を Select と揃えるための起点として使う.
            // (FILTER_ROW_SX.px = 1 = 8px = ICON_LEFT_PX なので展開のままで OK)
          }}
        />
        {!isActiveRecording && multiSelect && (
          <Box
            sx={{ mt: 0.75, display: 'flex', gap: 0.5, alignItems: 'center' }}
          >
            <Button
              size="small"
              variant="contained"
              startIcon={<MicIcon sx={{ fontSize: ICON_SIZE.sm }} />}
              disabled={selectedIds.size < 2}
              onClick={handleGroupRecordStart}
              sx={{
                fontSize: scaledFontSize('tag'),
                textTransform: 'none',
                py: 0.25,
                flex: 1,
              }}
            >
              {selectedIds.size < 2
                ? `${selectedIds.size}/2 名 選択`
                : `${selectedIds.size} 名で録音`}
            </Button>
            <Tooltip title="選択モードを終了" placement="top">
              <IconButton
                size="small"
                onClick={() => {
                  setMultiSelect(false)
                  setSelectedIds(new Set())
                }}
                aria-label="選択モードを終了"
                sx={{
                  width: 28,
                  height: 28,
                  color: colorTokens.fg.muted,
                }}
              >
                <CloseIcon sx={{ fontSize: ICON_SIZE.sm }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Section>
      <Section sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={16} />
          </Box>
        ) : filtered.length === 0 ? (
          <ComingSoonRow>該当する薬歴がありません</ComingSoonRow>
        ) : (
          <Stack spacing={0.25}>
            {pre.length > 0 && (
              <>
                <PhaseHeading
                  label="指導前"
                  count={pre.length}
                  expanded={phaseExpanded.pre}
                  onToggle={() => togglePhase('pre')}
                />
                {phaseExpanded.pre && pre.map(renderRow)}
              </>
            )}
            {post.length > 0 && (
              <>
                <PhaseHeading
                  label="指導済"
                  count={post.length}
                  expanded={phaseExpanded.post}
                  onToggle={() => togglePhase('post')}
                />
                {phaseExpanded.post && post.map(renderRow)}
              </>
            )}
            {done.length > 0 && (
              <>
                <PhaseHeading
                  label="完了"
                  count={done.length}
                  expanded={phaseExpanded.done}
                  onToggle={() => togglePhase('done')}
                />
                {phaseExpanded.done && done.map(renderRow)}
              </>
            )}
          </Stack>
        )}
      </Section>
    </Box>
  )
}
